import React, { useCallback, useEffect, useRef, useState } from "react";

import SchoolsCard from "../../components/cards/schoolsCard";
import { InternshipTypes } from "../../components/extra-type-page//internshipTypes";
import { ContentBlocks } from "../../components/extra-type-page/contentBlocks";
import DownloadableGuide from "../../components/extra-type-page/downloadableGuide";
import ProjectOverview from "../../components/location-page/projectOverview";
import ServiceInfo from "../../components/location-page/serviceInfo";
import FocusGviExperiences from "../../components/program-focus/focusGviExperiences";
import ProgramFocusHeadInfo from "../../components/program-focus/focusHeadInfo";
import ProgramExperiences from "../../components/program/programExperiences";
import EnquireCard from "../../components/shared/enquireCard";
import { FullwidthBannerBGImage } from "../../components/shared/fullwidthBannerBGImage";
import Testimonials from "../../components/shared/testimonials";
import TestimonialSection from "../../components/shared/testimonialSection";
import VolunteerProgramFocus from "../../components/volunteer-page/volunteerProgramFocus";
import ProgramsFiltered from "../../container/location-page/programsFiltered";
import { useBannerDetails } from "../../hooks/useBannerManager";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useFilterExperiencesByLocationSlug } from "../../hooks/useFilterExperiencesByLocationSlug";
import ArrowRight from "../../images/icons/arrow-right.inline.svg";
import TestiLightDesktopBg from "../../images/light-testimonial-bg-desktop.png";
import TestiLightMobileBg from "../../images/light-testimonial-bg-mobile.svg";
import TestiDarkDesktopBg from "../../images/research.png";
import TestiDarkMobileBg from "../../images/research.png";
import StickyFooter from "../shared/sticky/StickyWithEmbeddedModal";

const StickyLeftWrapper = ({ className, children }) => {
  return (
    <div className={`w-full h-full flex justify-center ${className}`}>
      <div className="container flex items-start w-11/12 md:w-3/4">
        <div className="w-full md:sticky-left-section md:pr-10">{children}</div>
      </div>
    </div>
  );
};

const ExtraTypePageComponent = ({
  page,
  genericDetails,
  customPrograms,
  layout = "default",
  pageProgramFilters,
  schoolTrips,
  theme
}) => {
  const containerRef = useRef(null);
  const stickyRef = useRef(null);
  const stickyBarRef = useRef(null);
  const breakpoint = useBreakpoint();
  const [applyNowLink, setApplyNowLink] = useState("/application-form/");
  const [openAlumniModal, setOpenAlumniModal] = useState(false);
  const [openBrochureModal, setOpenBrochureModal] = useState(false);

  const { colorMode, localeLanguage } = theme;

  useEffect(() => {
    const handleScroll = () => {
      const toppoint = containerRef.current?.getBoundingClientRect().top;
      const bottompoint = containerRef.current?.getBoundingClientRect().bottom;
      const cardTop = (window.innerHeight - 410) / 2;
      const cardHeight = 410;
      if (stickyRef.current) {
        if (toppoint > cardTop) {
          stickyRef.current.style.top = 0;
          stickyRef.current.style.bottom = "auto";
        } else if (bottompoint < cardHeight + cardTop) {
          stickyRef.current.style.top = "auto";
          stickyRef.current.style.bottom = 0;
        } else {
          stickyRef.current.style.top = cardTop - toppoint + "px";
          stickyRef.current.style.bottom = "auto";
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [stickyRef, containerRef]);

  useEffect(() => {
    const currentUrl = new URL(window.location);
    if (currentUrl?.href?.includes("group-travel")) {
      setApplyNowLink("/group-application-form/?type=application");
    }
  }, []);

  const { slug, title, acfLandingPage, content } = page ?? {};

  const headTitle = title ?? "";
  const acfFields = acfLandingPage ?? {};

  const { genFeaturedTestimonialVideosVertical } = acfFields ?? {};
  const imageGallery =
    (acfFields?.genFeaturedGallery &&
      acfFields.genFeaturedGallery.map((imgItem) => ({
        ...imgItem,
        type: "image"
      }))) ??
    [];

  useEffect(() => {
    const handleScroll = () => {
      if (stickyBarRef.current) {
        const top = containerRef.current.getBoundingClientRect().top;
        if (top >= window.innerHeight) {
          stickyBarRef.current.style.bottom = "-168px";
        } else {
          stickyBarRef.current.style.bottom = "0";
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
  }, []);

  const { globalProgramConfig, parentInfo } = genericDetails ?? {};

  const headSubtitle = acfFields?.genSubtitle ?? "";
  const secondayHeadline = acfFields?.genSecondaryHeadline ?? "";
  const programFeaturedVideo = "";
  const genFeaturedVideos = acfFields?.genFeaturedVideos ?? [];
  const programUri = "";
  const gotoViewText = "See gallery";
  const locationSummary = content ?? "";
  const tabDetails = acfFields?.genContentTabs?.filter((tab) => tab) ?? [];
  const keyMessageIcons = globalProgramConfig?.acfKeyMessagingIcons ?? [];
  const keyMessagingSet = acfFields?.genKeyMessagingIconset ?? "focus";

  const {
    fwpbBannerTitle,
    fwpbBannerCtaLabel,
    fwpbBannerCtaUri,
    genCfbSectionHeading,
    genCfbSectionTitle,
    genCfbList
  } = acfFields ?? {};

  const groupProgramSourceType =
    acfFields?.genProgramListSourceType === "group_programs";

  const programListFilterStrict =
    acfFields?.genProgramListFilterStrictValidation ?? false;

  let applyNow = genericDetails?.applyNow || "/application-form/";
  let enquireNow = genericDetails?.enquireNow || "/enquiry-form/";
  let applyCTALabel = "Apply";
  let enquireCTALabel = "Enquire";

  const getExperienceLocationPages =
    acfFields?.genProgramLocation?.flatMap(
      (loc) => loc?.locationPages?.nodes
    ) ?? [];

  const locationPageFilterSlugs =
    getExperienceLocationPages?.map((loc) => loc.slug) ?? [];

  const experiences = useFilterExperiencesByLocationSlug(
    locationPageFilterSlugs
  );

  const contentGridBlockTitle = acfFields?.genContentGridBlockTitle ?? "";

  const contentGridBlocks =
    acfFields?.genContentGridBlock?.map((block) => {
      return {
        title: block?.genCgbTitle ?? "",
        subtitle: block?.genCgbSubtitle ?? "",
        description: block?.genCgbSummary ?? "",
        imgUrl: block?.genCgbImage?.sourceUrl ?? "",
        imgMobileUrl: block?.genCgbImageMobile?.sourceUrl ?? "",
        icon: block?.genCgbIcon?.sourceUrl ?? "",
        linkTitle: block?.genCgbCtaTitle ?? "Learn more",
        externalLink: block?.genCgbExternalLink === true,
        url: block?.genCgbCtaLink ?? "",
        titleClass: "flex-1 text-black-lighter planet:text-white text-xl",
        subTitleClass:
          "h2 !text-xs !text-normal !font-normal !text-white p-2 bg-yellow-standard planet:bg-gray-standard rounded-t-md sm:rounded-md hidden sm:block",
        subTitleMobileClass:
          "h2 !text-sm !text-normal p-2 bg-yellow-standard planet:bg-gray-standard rounded-t-md sm:rounded-md sm:hidden absolute right-0 bottom-0 mx-2 w-1/2 !text-white text-center",
        imgHeight: "h-40",
        cardBoxCoverClass: "bg-transparent",
        descriptionClass:
          "sm:text-sm text-left text-black-lighter planet:text-gray-generic h-20"
      };
    }) ?? [];

  const genCfbListBlocks =
    genCfbList?.map((cfblock) => {
      return {
        title: cfblock?.genCfbListTitle ?? "",
        subtitle: cfblock?.genCfbListSubtitle ?? "",
        description: cfblock?.genCfbListContent ?? "",
        imgUrl: cfblock?.genCfbListImage?.sourceUrl ?? "",
        icon: "",
        linkTitle: cfblock?.genCfbListCtaTitle ?? "",
        url: cfblock?.genCfbListCtaTarget ?? "",
        titleClass: "flex-1 text-black-lighter planet:text-white text-xl",
        subTitleClass:
          "h2 !text-xs !text-normal !font-normal !text-white p-2 bg-yellow-standard planet:bg-gray-standard rounded-t-md sm:rounded-md hidden sm:block",
        subTitleMobileClass:
          "h2 !text-sm !text-normal p-2 bg-yellow-standard planet:bg-gray-standard rounded-t-md sm:rounded-md sm:hidden absolute right-0 bottom-0 mx-2 w-1/2 !text-white text-center",
        imgHeight: "h-56",
        cardBoxCoverClass: "bg-transparent",
        descriptionClass:
          "sm:text-sm text-left text-black-lighter planet:text-gray-generic"
      };
    }) ?? [];

  const { acfGenericGlobalConfig } = globalProgramConfig ?? {};

  const { ueStickySchedule } = acfGenericGlobalConfig ?? {};

  const {
    title: stickyTitle,
    image: stickyImage,
    desc: stickyDesc
  } = useBannerDetails(slug);
  let enquireCardDetails = {
    cardTitle: null,
    enquireCardImg: stickyImage,
    cardDesc:
      `<div>${stickyTitle || ""}</div><div class='font-normal font-body text-sm pt-4'>${stickyDesc || ""}</div>` ??
      "",
    cardBookDesc: null, //stickyBookDesc ?? "Book by"
    shareButton: false,
    language: localeLanguage
  };

  const downloadableGuide = {
    sectionTitle: acfFields?.genPpackSectionTitle ?? "",
    title: acfFields?.genPpackTitle ?? "",
    subTitle: acfFields?.genPpackSummary ?? "",
    imgUrl: acfFields?.genPpackImage?.sourceUrl ?? "",
    ctaLink: acfFields?.genPpackCtaLink ?? "",
    ctaTitle: acfFields?.genPpackCtaTitle ?? ""
  };

  const hasCommunityPrograms = acfFields?.genHasCommunityPrograms ?? false;

  const genericGlobalConfig = acfGenericGlobalConfig ?? {};

  const focusBlocks = page?.acfVolunteerAbroadFocusBlocks ?? [];

  const programSectionTitle =
    acfFields?.genProgramSectionTitle ?? "Our Programs";

  const expHeading = "Included in your program";
  const expSubHeading =
    "Local experiences exclusive to GVI in unique locations";
  const internshipExperiences = experiences?.slice(0, 8) ?? [];

  const testimonialHeading = "Talk to someone who's been there";
  const testimonialSubheading =
    "During office hours, we'll get back to you within a few minutes.";
  const testimonialCtaLabel = "Get more information";

  const experienceTitle = acfFields?.genExperiencesSectionExperienceTitle;
  const experienceSubtitle = acfFields?.genExperiencesSectionExperienceSubtitle;
  const experienceDescription =
    acfFields?.genExperiencesSectionExperienceDescription ??
    "Make the most of our unique programs with these exclusively curated local adventure and wellness experiences.";

  const teenExperiences = acfFields?.experiences?.slice(0, 8) ?? [];

  const parentInformationDetails =
    parentInfo?.parentInfo?.parentInformationDetails ?? "";
  const parentInformationParticipants =
    parentInfo?.parentInfo?.parentInformationParticipants?.length > 0
      ? parentInfo?.parentInfo?.parentInformationParticipants[0]
      : "";
  const isTeenPage = slug?.includes("teens");

  const {
    parentInfoParticipantsTitle,
    parentInfoParticipantsSubtitle,
    parentInfoParticipants
  } = parentInformationParticipants ?? {};

  const scholarships = genericGlobalConfig?.exclusiveDiscount ?? [];
  const scholarshipBackgroundImage =
    genericGlobalConfig?.exclusiveOfferBackgroundImage?.sourceUrl ?? null;
  const teenBrochureImage =
    genericGlobalConfig?.teenBrochureImage?.sourceUrl ?? null;
  const teenBrochureTitle =
    genericGlobalConfig?.teenBrochureTitle ??
    "Get up to EXCLUSIVE_OFFER off your program";
  const teenBrochureDescription =
    genericGlobalConfig?.teenBrochureDescription ??
    "When you join a climate research internship by 30 November";
  const teenBrochureDownloadLink =
    genericGlobalConfig?.teenBrochureDownloadLink ?? "";
  const scholarshipCtaLink =
    genericGlobalConfig?.exclusiveOfferLink ?? "/application-form";

  const currentDate = new Date();

  const teenStickyDetailsSchedule =
    ueStickySchedule
      ?.filter(
        (schedule) =>
          currentDate.getTime() >=
            Date.parse(
              schedule?.scheduledStartDate?.split("/")?.reverse()?.join("-")
            ) &&
          currentDate.getTime() <=
            Date.parse(
              schedule?.scheduledEndDate?.split("/")?.reverse()?.join("-")
            )
      )
      .shift() ?? {};

  const teenStickyDetails = {
    title: teenStickyDetailsSchedule?.stickyTitle ?? "",
    description: teenStickyDetailsSchedule?.stickyDescription ?? ""
  };

  const genTestimonialVideos =
    genFeaturedTestimonialVideosVertical?.slice(0, 3)?.map((video) => ({
      title: video?.genFeaturedTestimonialVideosVerticalVideoTitle ?? "",
      featVideos: video?.genFeaturedTestimonialVideosVerticalVideoUrl ?? "",
      date: video?.genFeaturedTestimonialVideosVerticalVideoDate ?? "",
      sourceUrl:
        video?.genFeaturedTestimonialVideosVerticalVideoBgImage?.sourceUrl ??
        "",
      instagram: {
        url: video?.genFeaturedTestimonialVideosVerticalVideoInstagram ?? "",
        className: isTeenPage
          ? "h-8 fill-white planet:fill-white"
          : "h-8 fill-black-lighter planet:fill-white",
        icon:
          video?.genFeaturedTestimonialVideosVerticalVideoInstagramIcon
            ?.sourceUrl ?? ""
      },
      linkedin: {
        url: video?.genFeaturedTestimonialVideosVerticalVideoLinkedin ?? "",
        className: isTeenPage
          ? "h-8 fill-white planet:fill-white"
          : "h-8 fill-black-lighter planet:fill-white",
        icon:
          video?.genFeaturedTestimonialVideosVerticalVideoLinkedinIcon
            ?.sourceUrl ?? ""
      },

      extraSocialContainerClass: "px-0 py-3 md:py-5"
    })) ?? [];

  const participants = {
    title: parentInfoParticipantsTitle ?? "",
    subTitle: parentInfoParticipantsSubtitle ?? "",
    blocks:
      parentInfoParticipants?.map((item) => ({
        link: item?.url ?? "#",
        site: item?.title ?? "",
        count: item?.review ?? "",
        logo: item?.logo ?? ""
      })) ?? []
  };

  const getFilterByProgramType =
    pageProgramFilters?.getFilterByProgramType ?? [];
  if (
    !genericDetails?.isUniversity &&
    getFilterByProgramType &&
    getFilterByProgramType?.length > 0
  ) {
    applyNow = `/application-form/?program_type=${getFilterByProgramType}`;
  }

  if (groupProgramSourceType === true) {
    applyNow = `/application-form/?group_source=${true}`;
    enquireNow = `/enquiry-form/?type=group_enquiry`;
    applyCTALabel = "Student <br/> Application";
    enquireCTALabel = "Educator <br/> Enquiry";
    enquireCardDetails = {
      cardTitle: stickyTitle ?? "",
      enquireCardImg: stickyImage,
      cardDesc: "",
      cardBookDesc: ""
    };
  }

  const programFilterListConfig = isTeenPage
    ? {
        sectionDefaultTitle: programSectionTitle,
        sectionTitle: programSectionTitle,
        locationData: acfFields?.genProgramLocation ?? [],
        showCommunityPrograms: hasCommunityPrograms,
        customPrograms: customPrograms,
        groupProgramSourceType: groupProgramSourceType,
        pageProgramFilters: pageProgramFilters,
        programListFilterStrict: programListFilterStrict,
        programListContainerClass:
          "bg-yellow-lighter planet:bg-gray-darker !px-6 pt-14 pb-20 sm:pt-0 sm:px-0 sm:pb-10",
        programListPaginationGradientClass:
          "before:bg-yellow-lighter planet:before:bg-gray-darker",
        titleStyle: "md:bg-gray-tertiary planet:md:bg-gray-standard",
        sectionTitleStyle: `${
          isTeenPage
            ? "bg-transparent sm:bg-gray-tertiary planet:sm:bg-gray-light"
            : "sm:bg-gray-tertiary planet:bg-gray-light"
        }  items-center`,
        programListPaginationGradientDefaultClass:
          "before:absolute before:w-full before:h-28 before:bottom-0 before:z-20 before:masked-overview before:masked-overview-top",
        sectionContainerClass: "bg-yellow-lighter",
        programListConfig: {
          programlistPrimaryProgramsSliderContainer: "-mr-6 sm:mr-0",
          programlistPrimarySecondarySliderContainer: "-mr-6 sm:mr-0",
          programFilterMobileMenuCTAClass:
            "xxs:bg-yellow-standard planet:bg-gray-darker"
        }
      }
    : {
        sectionDefaultTitle: programSectionTitle,
        sectionTitle: programSectionTitle,
        locationData: acfFields?.genProgramLocation ?? [],
        showCommunityPrograms: hasCommunityPrograms,
        customPrograms: customPrograms,
        groupProgramSourceType: groupProgramSourceType,
        pageProgramFilters: pageProgramFilters,
        programListFilterStrict: programListFilterStrict,
        programListContainerClass:
          "bg-yellow-lighter planet:bg-gray-darker !px-6 pt-14 sm:pt-0 sm:px-0 sm:pb-10",
        programListPaginationGradientClass:
          "before:bg-yellow-lighter planet:before:bg-gray-darker",
        titleStyle: "md:bg-gray-tertiary planet:md:bg-gray-standard",
        sectionTitleStyle:
          "sm:bg-gray-tertiary planet:bg-gray-light items-center",
        programListPaginationGradientDefaultClass:
          "before:absolute before:w-full before:h-28 before:bottom-0 before:z-20 before:masked-overview before:masked-overview-top",
        sectionContainerClass: "bg-yellow-lighter",
        programListConfig: {
          programFilterMobileMenuCTAClass:
            "xxs:bg-yellow-standard planet:bg-gray-darker"
        }
      };

  const previousSchools = {
    showSchools: acfFields?.genSchoolTripsShow ?? "",
    title: acfFields?.genSchoolTripsSectionTitle ?? ""
  };

  const [visibleCnt, setVisibleCnt] = useState(6);
  const [pagination, setPagination] = useState(true);

  useEffect(() => {
    setVisibleCnt(6);
  }, [breakpoint]);

  useEffect(() => {
    if (visibleCnt > schoolTrips?.length) setPagination(false);
  }, [schoolTrips?.length, visibleCnt]);

  const handlePagination = useCallback(() => {
    setVisibleCnt((prevState) => prevState + 6);
  }, []);

  return (
    <>
      <ProgramFocusHeadInfo
        key={"extra-type-program-head-info"}
        headTitle={headTitle}
        headSubtitle={headSubtitle}
        programFeaturedVideo={programFeaturedVideo}
        programUri={programUri}
        gallery={imageGallery}
        gotoViewText={gotoViewText}
        locationSummary={locationSummary}
        genFeaturedVideos={genFeaturedVideos}
        parentInformationDetails={parentInformationDetails}
        setOpenModal={setOpenAlumniModal}
        openModal={openAlumniModal}
        freeParentBtnClass="flex gap-2 items-center w-full md:w-max justify-center text-sm font-semibold leading-7 tracking-wider px-7"
        freeParentIconClass="stroke-white group-hover:stroke-white"
        sectionBgClass="relative flex flex-col items-center justify-center px-4 py-10 mx-auto md:pt-20 md:pb-10 md:px-0 headinfo-section bg-gray-lighten planet:bg-gray-darkest"
        showParentInfo={isTeenPage}
        viewProgramCTAPreset={"secondary"}
        freeParentArrowSize={
          isTeenPage ? (colorMode === "planet" ? "std" : "sml") : "std"
        }
        ctaButtonClass={`${
          layout === "internship"
            ? "bg-white border-black-lighter border hover:border-white text-white text-base planet:text-sm font-semibold leading-7 tracking-wider gap-2"
            : isTeenPage
              ? "bg-white border-generic-text border text-black-lighter w-full md:w-max justify-center px-7 text-sm font-semibold leading-7 tracking-wider gap-2 planet:bg-gray-darkest"
              : "bg-white border-generic-text border text-black-lighter text-base planet:text-sm font-semibold leading-7 tracking-wider gap-2 px-7"
        }`}
        ctaIconClass={`${
          layout === "internship"
            ? "fill-black-lighter planet:fill-white planet:stroke-gray-darker planet:stroke-transparent group-hover:fill-white planet:group-hover:fill-gray-darker planet:group-hover:stroke-transparent"
            : isTeenPage
              ? "fill-black-lighter stroke-transparent group-hover:fill-white group-hover:stroke-transparent planet:fill-white planet:stroke-transparent planet:group-hover:fill-gray-darker planet:group-hover:stroke-transparent"
              : "fill-black-lighter planet:fill-white planet:stroke-gray-darker planet:stroke-transparent group-hover:fill-white planet:group-hover:fill-gray-darker planet:group-hover:stroke-transparent"
        }`}
        hasCollapsibleWrapper={!isTeenPage && layout !== "internship"}
        canShare={layout !== "internship"}
        sectionContainerStyles={`px-0 py-10 md:pt-20 md:pb-10 md:px-0 ${
          layout === "internship"
            ? "bg-secondary-bg planet:bg-generic-bg"
            : "bg-generic-bg"
        }`}
      />
      <div className="relative sticky-wrapper">
        <ServiceInfo
          keyIcons={keyMessageIcons}
          iconset={keyMessagingSet}
          sectionContainerStyles={`${
            layout === "internship" ? "bg-secondary-bg" : "bg-generic-bg"
          }`}
          imageContainerClass={"bg-yellow-standard"}
          scholarships={scholarships}
          scholarshipBackgroundImage={scholarshipBackgroundImage}
          scholarshipFeatureImage={teenBrochureImage}
          scholarshipTitle={teenBrochureTitle}
          scholarshipDescription={teenBrochureDescription}
          scholarshipCtaLink={teenBrochureDownloadLink ?? scholarshipCtaLink}
          serviceInfoSectionClass="flex flex-col items-center px-4 py-6 sm:py-12 md:px-0 summary-section planet:bg-gray-darkest bg-generic-bg"
          showBrochure={isTeenPage}
          setOpenModal={setOpenBrochureModal}
          openModal={openBrochureModal}
        />

        <div className="flex flex-col sm:flex-none">
          <ProjectOverview
            subtitle={secondayHeadline}
            tabsDetails={tabDetails}
            locationData={acfFields?.genProgramLocation}
            locationPageSlugs={locationPageFilterSlugs}
            direction="vertical"
            reviewTitle={participants?.title}
            reviewSubTitle={participants?.subTitle}
            reviewBlocks={participants?.blocks}
            showParticipants={isTeenPage}
            sectionContainerClass={`relative flex items-center justify-center px-4 pb-12 md:px-0 overflow-hidden ${
              isTeenPage ? "order-5 sm:order-none" : ""
            }`}
          />

          {layout === "internship" &&
            internshipExperiences &&
            internshipExperiences.length > 0 && (
              <ProgramExperiences
                experiences={internshipExperiences}
                expHeading={expHeading}
                expSubHeading={expSubHeading}
              />
            )}

          {layout === "default" && contentGridBlocks.length > 0 && (
            <StickyLeftWrapper className="bg-white planet:bg-gray-standard sm:pb-24">
              <ContentBlocks
                title={contentGridBlockTitle}
                blocks={contentGridBlocks}
              />
            </StickyLeftWrapper>
          )}

          <div
            className={`hidden w-full md:absolute h-[410px] md:h-full md:sticky-position md:!top-8 md:w-auto sm:flex md:!right-[9.5%] pb-10 ${
              isTeenPage
                ? "order-0 sm:order-none planet:bg-gray-darkest bg-generic-bg sm:bg-transparent planet:sm:bg-transparent pt-10 pb-20 sm:pt-0 sm:pb-0"
                : ""
            }`}
          >
            <div
              className="relative w-11/12 md:w-[355px] mx-auto sm:m-0 py-4 sm:py-0"
              ref={containerRef}
            >
              <div
                ref={stickyRef}
                className={`card-sticky-container relative sm:absolute`}
              >
                <EnquireCard
                  applyNow={applyNow}
                  enquireNow={enquireNow}
                  enquireCardDetails={enquireCardDetails}
                  applyCTALabel={applyCTALabel}
                  enquireCTALabel={enquireCTALabel}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {previousSchools?.showSchools &&
        schoolTrips &&
        schoolTrips?.length > 0 && (
          <div
            className="bg-gray-lighten planet:bg-gray-darkest"
            id={"view-schools"}
          >
            <div className="relative w-full mx-auto sm:w-4/5 pt-16 pb-16 px-4 sm:px-0">
              <h1
                className="w-4/5 sm:w-full text-[26px] sm:text-[38px] font-normal planet:font-light font-alice planet:font-body planet:text-white pb-8"
                dangerouslySetInnerHTML={{ __html: previousSchools?.title }}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-3 gap-y-8 max-h-[32rem] sm:max-h-full gvi-scrollbar-thin tab-content_html masked-overview masked-overview-down pr-2 sm:pr-0">
                {schoolTrips &&
                  schoolTrips?.length > 0 &&
                  schoolTrips?.slice(0, visibleCnt)?.map((schoolTrip, idx) => {
                    const title = schoolTrip?.title;
                    const slug = schoolTrip?.slug;
                    const subTitle = schoolTrip?.acfSchoolTrips?.stpSubtitle;
                    const bgImage = schoolTrip?.featuredImage?.node?.sourceUrl;
                    const location = schoolTrip?.acfSchoolTrips?.stpLocation;
                    const tripDate = schoolTrip?.acfSchoolTrips?.stpTripDate;

                    return (
                      <SchoolsCard
                        slug={slug}
                        schoolTitle={title}
                        subTitle={subTitle}
                        bgImage={bgImage}
                        location={location}
                        tripDate={tripDate}
                        isRounded={true}
                        isRoundedTop={false}
                        bgColor={"bg-white planet:bg-black-dark h-full w-full"}
                        key={`school-trip-${idx}`}
                      />
                    );
                  })}

                {schoolTrips?.length > visibleCnt && (
                  <div
                    className={
                      "col-span-full w-full md:px-0 px-10 pt-10 flex justify-center items-center"
                    }
                  >
                    <button
                      onClick={() => handlePagination()}
                      className="group flex flex-row items-center justify-center px-2 py-2 w-48
                bg-white planet:bg-transparent hover:bg-black-lighter planet:hover:bg-white
                border border-black-lighter hover:border-white planet:border-white planet:hover:border-white rounded-full planet:rounded-sm
                text-sm text-black-lighter hover:text-white planet:text-white planet:hover:text-gray-darker"
                    >
                      <p className="text-base font-bold pr-4">View more</p>
                      <ArrowRight className="w-3 h-3 stroke-black-lighter group-hover:stroke-white planet:stroke-white planet:group-hover:stroke-gray-darker rotate-90" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      {layout === "internship" && genCfbListBlocks?.length > 0 && (
        <InternshipTypes
          title={genCfbSectionHeading}
          subtitle={genCfbSectionTitle}
          subtitleContainerClass={
            "pb-8 md:h-24 md:w-full mb-10 mt-20 sm:m-0 sm:pt-20"
          }
          subtitleClass={"text-left text-generic-text planet:text-white"}
          titleStyle={"hidden sm:block h6 planet:font-normal border-none pb-2"}
          titleClass={"flex-1 text-black-lighter planet:text-white text-xl"}
          blocks={genCfbListBlocks}
          headingClass={
            "text-left sm:text-center text-generic-text planet:text-white pt-2"
          }
          headingOuterContainerClass={""}
          headingContainerClass={
            "md:h-24 md:w-8/12 md:-mt-12 md:bg-gray-tertiary planet:md:bg-gray-darker hidden sm:block"
          }
          contentClass={"my-6"}
          containerClass={
            "px-4 bg-yellow-lighter planet:gvi-black-menu mt-16 md:mt-0 "
          }
          contentWrapperClass={
            "flex flex-col p-6 pb-10 rounded-b bg-white planet:gvi-dark-gray-bg"
          }
          outerContainerClass={
            "planet:border-t border-b md:border-none border-t-gray-lighter border-b-gray-lighter pb-10 sm:pb-6 "
          }
          descriptionContainerClass={"pt-4 pb-8"}
          conditionalSliderOpts={{
            condition: breakpoint === "xs",
            slidesPerView: 1.15,
            spaceBetween: 10,
            centeredSlides: false,
            className: "w-full h-full"
          }}
        />
      )}

      {layout === "internship" && contentGridBlocks?.length > 0 && (
        <InternshipTypes
          title={contentGridBlockTitle}
          titleStyle={"h6 planet:font-normal border-none pb-2"}
          titleClass={"flex-1 text-black-lighter planet:text-white text-xl"}
          blocks={contentGridBlocks}
          containerClass={
            "bg-yellow-lighter planet:gvi-black-menu pb-20 sm:pb-24 pt-20 sm:pt-10 tab-content px-4"
          }
          cardCtaContainerClass={"md:w-1/2"}
          headingContainerClass={"md:h-24 md:w-full mb-10 sm:m-0"}
          headingClass={
            "text-left text-generic-text planet:text-white flex items-center"
          }
          headingOuterContainerClass={""}
          contentClass={"my-10"}
          descriptionContainerClass={"pt-3 pb-4 sm:pb-0"}
          conditionalSliderOpts={{
            condition: breakpoint === "xs",
            slidesPerView: 1.15,
            spaceBetween: 10,
            centeredSlides: false,
            className: "w-full h-full"
          }}
        />
      )}

      {layout === "internship" && (
        <VolunteerProgramFocus
          hasCommunityPrograms={hasCommunityPrograms}
          focusBlocks={focusBlocks}
          containerClass={
            "pb-14 sm:py-20 pt-12 sm:pt-6 bg-white planet:bg-gray-darkest sm:planet:gvi-light-gray-bg planet:border-t planet:border-b md:mt-0 md:border-none border-t-gray-lighter border-b-gray-lighter"
          }
          headingClass={
            "w-10/12 py-2 h-24 md:w-8/12 mb-10 bg-gray-tertiary planet:gvi-dark-gray-bg"
          }
          titleClass={"text-center"}
          headingContainerClass={"static flex flex-col w-full -top-12 absolute"}
          cardBoxCoverClass={"sm:gvi-program-card-box bg-transparent"}
          descriptionContainerClass={"pt-3 pb-4 h-24 sm:h-auto"}
          contentWrapperClass={
            "flex flex-col p-6 rounded-b bg-white planet:gvi-dark-gray-bg border planet:border-0 border-light hover:border-0 "
          }
        />
      )}

      {layout === "internship" && fwpbBannerTitle && (
        <FullwidthBannerBGImage
          bannerTitle={fwpbBannerTitle}
          bannerTitleClass={
            "banner-heading no-br sm:show-br leading-none h1 text-left sm:text-center !font-normal"
          }
          containerClass={
            "bg-generic-bg planet:bg-gray-darkest sm:px-4 sm:py-12 h-full sm:h-97 overflow-hidden relative"
          }
          showCTAButton={true}
          buttonStyle={
            "group relative z-10 flex flex-row items-center justify-center px-2 py-2 rounded-full planet:rounded-sm text-gray-darker bg-white planet:bg-gray-darkest hover:bg-black-lighter planet:hover:text-gray-darker w-full border-black-lighter planet:border-white border"
          }
          labelClass={
            "text-base leading-genericCta font-bold pr-2 text-black-lighter planet:text-white group-hover:text-white planet:group-hover:text-gray-darker"
          }
          arrowClass={
            "group-hover:stroke-white planet:group-hover:stroke-gray-darker stroke-black-lighter planet:stroke-white"
          }
          ctaLabel={fwpbBannerCtaLabel}
          ctaUri={fwpbBannerCtaUri}
          bannerInnerContainerClass={"w-full pt-4 sm:pt-0"}
          showGradient={false}
          showBGImage={false}
        />
      )}

      {layout === "internship" && (
        <Testimonials
          testimonials={[]}
          heading={testimonialHeading}
          subHeading={testimonialSubheading}
          ctaLabel={testimonialCtaLabel}
          type="program"
          form_type="enquiry_form"
          layout={layout}
          bgSrc={{
            light: {
              desktop: TestiLightDesktopBg,
              mobile: TestiLightMobileBg
            },
            planet: { desktop: TestiDarkDesktopBg, mobile: TestiDarkMobileBg }
          }}
        />
      )}
      {layout === "default" && (
        <>
          <ProgramsFiltered {...programFilterListConfig} />

          {genTestimonialVideos && genTestimonialVideos?.length > 0 && (
            <TestimonialSection
              genTestimonialVideos={genTestimonialVideos}
              sliderSpaceBetween={10}
              sliderSlidesPerView={1}
              sliderPagination={true}
              isTeenPage={isTeenPage}
            />
          )}

          {teenExperiences && teenExperiences?.length > 0 && (
            <FocusGviExperiences
              genExperiencesSectionExperienceTitle={experienceTitle}
              genExperiencesSectionExperienceSubtitle={experienceSubtitle}
              genExperiencesSectionExperienceDescription={experienceDescription}
              experiences={teenExperiences}
            />
          )}
        </>
      )}

      {layout === "default" &&
        (downloadableGuide.title || downloadableGuide.subTitle) && (
          <DownloadableGuide
            guide={downloadableGuide}
            guideClass={
              "relative flex flex-col items-center justify-center px-4 py-20 mx-auto md:pt-20 md:pb-20 md:px-0 headinfo-section bg-gray-lighten planet:bg-gray-dark"
            }
            guideContentWrapper={
              "flex flex-col gap-10 md:flex-row pt-8 sm:py-16"
            }
          />
        )}

      {(teenStickyDetails?.title || teenStickyDetails?.description) &&
        isTeenPage && (
          <StickyFooter
            teenStickyDetails={teenStickyDetails}
            applyNow={applyNow}
            setOpenModal={setOpenAlumniModal}
            ref={stickyBarRef}
            containerRefClass={""}
          />
        )}
    </>
  );
};

export default ExtraTypePageComponent;
